import { useQueryClient } from "@tanstack/react-query"

import { useExtendWithSubnetworks, useNetworksStateContext } from "@l2r-front/l2r-networks"

import { API_MODULE_RISK_ZONE } from "../../../../common/constants/modules"
import { useRiskZonesStateContext } from "../../contexts/RiskZonesContext"

import { riskZonesQueryKeys } from "./queryKeys"
import { useRiskZonesQuery } from "./useRiskZonesQueries"

export function useRiskZoneClusters(parameters = {}, config = {}) {
    const url = "risk_zone_clusters/"
    const { selectedNetwork } = useNetworksStateContext()

    const queryParameters = useExtendWithSubnetworks({
        ...parameters,
        project: selectedNetwork.slug,
    }, API_MODULE_RISK_ZONE)

    const { enabled = true } = config
    const queryKey = riskZonesQueryKeys["layer"](queryParameters)

    return useRiskZonesQuery(
        url,
        queryKey,
        queryParameters,
        {
            ...config,
            enabled: enabled && !!selectedNetwork,
        },
    )
}

export function useRiskZoneCluster(clusterId, parameters = {}, config = {}) {
    const url = "risk_zone_clusters/"
    const { selectedNetwork } = useNetworksStateContext()
    const { currentYear } = useRiskZonesStateContext()

    const queryParameters = {
        ...parameters,
        project: selectedNetwork.slug,
        identifier: clusterId,
        ...(currentYear && { year: currentYear }),
    }

    const { enabled = true } = config
    const queryKey = riskZonesQueryKeys["feature"](clusterId, parameters)

    return useRiskZonesQuery(
        url,
        queryKey,
        queryParameters,
        {
            ...config,
            autoRefetch: true,
            enabled: enabled && !!selectedNetwork,
        },
    )
}

export const useExistingRiskZoneCluster = (clusterIdentifier, onError) => {
    const riskZoneQueryKey = riskZonesQueryKeys["feature"](clusterIdentifier)
    riskZoneQueryKey.pop()
    const layerQueryKey = riskZonesQueryKeys["layer"]()
    layerQueryKey.pop()
    const queryClient = useQueryClient()

    const riskZonesDataFromLayer = queryClient.getQueriesData({ queryKey: layerQueryKey })
    const riskZonesData = queryClient.getQueriesData({ queryKey: riskZoneQueryKey })

    const existingInRiskZonesData = riskZonesData?.some(([_, riskZone]) => {
        return (riskZone?.risk_zone?.identifier) === clusterIdentifier
    })
    const existingInRiskZoneLayerData = riskZonesDataFromLayer?.some(([_, riskZoneDataFromLayer]) => {
        if (riskZoneDataFromLayer?.features) {
            return riskZoneDataFromLayer.features?.some(riskZone => riskZone.properties?.risk_zone_identifier === clusterIdentifier)
        } else {
            return riskZoneDataFromLayer?.some(riskZone => riskZone.properties?.risk_zone_identifier === clusterIdentifier)
        }
    })

    const existingRiskZoneInCache = existingInRiskZonesData || existingInRiskZoneLayerData
    const { data, isLoading, isError } = useRiskZoneCluster(clusterIdentifier, {}, {
        enabled: !existingRiskZoneInCache,
        onError,
    })

    if (!isLoading && !isError && data && !data.length && !existingRiskZoneInCache) {
        onError()
    }

    return (!(isLoading || isError) || existingRiskZoneInCache)
}